import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useDepositIncoming } from '@/hooks/useDepositIncoming';
import type { DepositIncomingCard } from '@/supabase/deposit/types/deposit';

interface DepositIncomingContextValue {
  // 그리드 행(order_${id})에서 조회: 매칭 카드 or undefined.
  depositByOrderId: Map<number, DepositIncomingCard>;
  // 현재 그리드(customerTemplates)에 행이 없는 입금 카드 — 상단 폴백 전용.
  fallbackCards: DepositIncomingCard[];
  confirm: (card: DepositIncomingCard) => void;
  reject: (card: DepositIncomingCard) => void;
  // 방금 확정한 카드를 5초 인라인 표시에서 즉시 되돌림(상태+다리학습 원복).
  undo: (card: DepositIncomingCard) => void;
  confirming: boolean;
  // 진행 중 액션의 카드/종류 — 스피너를 누른 버튼에만 표시(codexloop #5).
  pendingMatchId: number | null;
  pendingAction: 'confirm' | 'reject' | null;
  justArrivedMatchIds: Set<number>;
  // 방금 확정된 카드 matchId — 칩/카드를 '확정됨 · 되돌리기' 모드로 렌더할지 판정.
  confirmedMatchIds: Set<number>;
}

const EMPTY: DepositIncomingContextValue = {
  depositByOrderId: new Map(),
  fallbackCards: [],
  confirm: () => {},
  reject: () => {},
  undo: () => {},
  confirming: false,
  pendingMatchId: null,
  pendingAction: null,
  justArrivedMatchIds: new Set(),
  confirmedMatchIds: new Set(),
};

const DepositIncomingContext = createContext<DepositIncomingContextValue>(EMPTY);

export function useDepositIncomingContext() {
  return useContext(DepositIncomingContext);
}

interface ProviderProps {
  selectedDate: Date;
  refreshOrders: () => Promise<void>;
  // 현재 그리드가 보여줄 수 있는 주문 id 집합(customerTemplates에서 derive). 폴백 판정용.
  gridOrderIds: Set<number>;
  children: ReactNode;
}

// ★React.memo 금지: 이 provider는 useDepositIncoming(realtime) 라이브 상태를 소유한다.
// memo로 감싸면 children(Home이 만든 그리드 element)이 stale될 수 있다.
export function DepositIncomingProvider({ selectedDate, refreshOrders, gridOrderIds, children }: ProviderProps) {
  const { cards, confirm, reject, confirming, confirmingId, pendingAction, justArrivedIds, confirmedMatchIds, undoConfirm } = useDepositIncoming(selectedDate, refreshOrders);

  const value = useMemo<DepositIncomingContextValue>(() => {
    const byOrder = new Map<number, DepositIncomingCard>();
    const fallback: DepositIncomingCard[] = [];
    // ★확정 카드를 앞에 정렬 → byOrder는 first-wins라 확정 카드가 같은 orderId 행을 우선 차지한다.
    //   (한 주문에 pending 입금이 또 있어도 '확정됨'이 클릭한 행을 지키고 pending은 fallback으로 밀린다.)
    //   confirmed가 없으면 stable-sort라 순서 불변 → 기존 동작과 동일(behavior-inert).
    const ordered = [...cards].sort(
      (a, b) => (confirmedMatchIds.has(a.matchId) ? 0 : 1) - (confirmedMatchIds.has(b.matchId) ? 0 : 1),
    );
    for (const c of ordered) {
      // gridOrderIds = 미지정·non-keep 행의 주문 id만(Home에서 좁힘) → 비-미지정 매칭은 fallback으로.
      // 같은 orderId에 입금이 2건↑이면 첫 건만 행 칩, 나머지는 fallback(상단 카드)으로 — 조용한 소실 방지.
      // ★확정 직후 카드는 그 주문 행에 고정: 확정으로 주문이 입금완료가 되어 gridOrderIds에서 빠져도
      //   원래 행(order_${id})에 '확정됨 · 되돌리기'를 5초 표시한다(오너 결정: '칩이 있던 그 자리').
      const pinToRow = confirmedMatchIds.has(c.matchId) || gridOrderIds.has(c.orderId);
      if (pinToRow && !byOrder.has(c.orderId)) byOrder.set(c.orderId, c);
      else fallback.push(c);
    }
    return { depositByOrderId: byOrder, fallbackCards: fallback, confirm, reject, undo: undoConfirm, confirming, pendingMatchId: confirmingId, pendingAction, justArrivedMatchIds: justArrivedIds, confirmedMatchIds };
  }, [cards, gridOrderIds, confirm, reject, undoConfirm, confirming, confirmingId, pendingAction, justArrivedIds, confirmedMatchIds]);

  return <DepositIncomingContext.Provider value={value}>{children}</DepositIncomingContext.Provider>;
}
